import { Download, Eye, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { ScrollArea } from '../../ui/scroll-area';
import { Card } from '../../ui/card';

import type { ShortcutRunRecord } from './types';
import { getShortcutStatusTone } from './types';

type ShortcutHistoryDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runs: ShortcutRunRecord[];
  onRefresh: () => void;
  onOpenReview: (run: ShortcutRunRecord) => void;
  onDownloadResult: (run: ShortcutRunRecord) => void;
  onDownloadArtifact: (run: ShortcutRunRecord, artifact: string) => void;
  isRefreshing?: boolean;
};

const toneClasses = {
  amber: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  blue: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  green: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  red: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  slate: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
} as const;

function formatTimestamp(value: string | null) {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleString();
}

export default function ShortcutHistoryDialog({
  open,
  onOpenChange,
  runs,
  onRefresh,
  onOpenReview,
  onDownloadResult,
  onDownloadArtifact,
  isRefreshing = false,
}: ShortcutHistoryDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-hidden border-slate-700 bg-slate-950 text-slate-100">
        <DialogHeader className="flex-row items-start justify-between gap-4 space-y-0 pr-10">
          <div>
            <DialogTitle className="text-xl">Shortcut history</DialogTitle>
            <DialogDescription className="text-slate-400">
              MapleSheet stores local Shortcut metadata here so active and completed runs can be reopened across sessions.
            </DialogDescription>
          </div>
          <Button type="button" variant="outline" className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800" onClick={onRefresh}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </DialogHeader>

        <ScrollArea className="h-[70vh] pr-3">
          <div className="space-y-3">
            {runs.length === 0 ? (
              <Card className="border-slate-800 bg-slate-900/70 p-6 text-sm text-slate-400">
                No Shortcut jobs have been submitted from MapleSheet yet.
              </Card>
            ) : (
              runs.map((run) => {
                const tone = getShortcutStatusTone(run.status);
                return (
                  <Card key={run.id} className="space-y-4 border-slate-800 bg-slate-900/70 p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-slate-100">{run.source_file_name || 'Current MapleSheet'}</span>
                          <Badge className={toneClasses[tone]}>{run.status}</Badge>
                          <Badge className="bg-slate-800 text-slate-200">{run.applied_action}</Badge>
                        </div>
                        <p className="text-sm text-slate-300">{run.prompt}</p>
                        <div className="grid gap-2 text-xs text-slate-500 sm:grid-cols-2 xl:grid-cols-4">
                          <div>Selection: {run.selected_range || 'Used sheet'}</div>
                          <div>Created: {formatTimestamp(run.created_at)}</div>
                          <div>Completed: {formatTimestamp(run.completed_at)}</div>
                          <div>Run ID: <span className="font-mono text-slate-400">{run.shortcut_run_id}</span></div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="outline" className="border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800" onClick={() => onOpenReview(run)}>
                          <Eye className="mr-2 h-4 w-4" />
                          Review
                        </Button>
                        {run.result_download_available ? (
                          <Button type="button" variant="outline" className="border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800" onClick={() => onDownloadResult(run)}>
                            <Download className="mr-2 h-4 w-4" />
                            Result
                          </Button>
                        ) : null}
                      </div>
                    </div>

                    {run.artifacts_json.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {run.artifacts_json.map((artifact) => (
                          <Button
                            key={artifact}
                            type="button"
                            variant="outline"
                            className="border-slate-800 bg-slate-950 text-slate-300 hover:bg-slate-800"
                            onClick={() => onDownloadArtifact(run, artifact)}
                          >
                            <Download className="mr-2 h-4 w-4" />
                            {artifact}
                          </Button>
                        ))}
                      </div>
                    ) : null}
                  </Card>
                );
              })
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
